export const STUDIO_PAGE_SECTION_TYPES = [
	'hero',
	'markdown',
	'form_embed',
	'product_showcase',
	'coverage_grid',
	'cta',
	'stats',
	'separator',
	'faq',
	'testimonials',
	'image_text',
	'logos',
	'blog_listing',
	'how_it_works',
	'comparison_table',
	'pricing_table',
	'document_downloads',
	'trust_badges',
	'team_grid',
	'video_embed',
	'app_download',
	'branch_locator',
	'custom_html',
] as const

/** Increment only when a component schema requires a newer tenant renderer. */
export const PAGE_BUILDER_CONTRACT_VERSION = 1

export type StudioPageSectionType = (typeof STUDIO_PAGE_SECTION_TYPES)[number]

export interface StudioSectionStyleConfig {
	[key: string]: unknown
	bg_color?: string
	text_color?: string
	padding_top?: number
	padding_bottom?: number
	align?: '' | 'left' | 'center' | 'right'
	max_width?: '' | 'narrow' | 'normal' | 'wide' | 'full'
	anchor_id?: string
	responsive?: Partial<
		Record<'mobile' | 'tablet', Partial<StudioSectionStyleConfig>>
	>
}

export interface StudioHeroConfig extends StudioSectionStyleConfig {
	heading?: string
	subheading?: string
	cta_text?: string
	cta_form_code?: string
	cta_link?: string
	bg_color?: string
	text_color?: string
	image_url?: string
}

export interface StudioProductShowcaseConfig extends StudioSectionStyleConfig {
	product_code?: string
	cta_text?: string
	cta_form_code?: string
	show_premium?: boolean
	show_coverages?: boolean
	show_cta?: boolean
}

export interface StudioCoverageGridConfig extends StudioSectionStyleConfig {
	product_code?: string
	title?: string
	columns?: number
}

export interface StudioPricingPlan {
	name?: string
	price?: string
	period?: string
	description?: string
	features?: string[]
	cta_text?: string
	cta_form_code?: string
	highlighted?: boolean
}

export interface StudioPricingTableConfig extends StudioSectionStyleConfig {
	title?: string
	plans?: StudioPricingPlan[]
}

export interface StudioFaqConfig extends StudioSectionStyleConfig {
	title?: string
	items?: Array<{ q?: string; a?: string }>
}

export interface StudioCtaConfig extends StudioSectionStyleConfig {
	heading?: string
	subheading?: string
	button_text?: string
	button_form_code?: string
	button_link?: string
	bg_color?: string
}

export interface StudioPageSectionConfigMap {
	hero: StudioHeroConfig
	product_showcase: StudioProductShowcaseConfig
	coverage_grid: StudioCoverageGridConfig
	pricing_table: StudioPricingTableConfig
	faq: StudioFaqConfig
	cta: StudioCtaConfig
}

export type StudioPageSectionConfig<T extends StudioPageSectionType> =
	T extends keyof StudioPageSectionConfigMap
		? StudioPageSectionConfigMap[T]
		: StudioSectionStyleConfig & Record<string, unknown>

/** Canonical visual-regression content. Renderers use this exact authored
 * payload in stories/tests so framework-specific implementations are compared
 * against the same states rather than hand-written lookalikes. */
export const STUDIO_PAGE_COMPONENT_FIXTURES = {
	hero: { heading: 'Protection built around you', subheading: 'Simple cover, clear choices.', cta_text: 'Get a quote', cta_form_code: 'fixture_quote', image_url: '/fixtures/hero.jpg' },
	markdown: { content: '## Cover with confidence\nClear terms and practical support.' },
	form_embed: { form_code: 'fixture_quote', title: 'Start your quote', description: 'Takes about two minutes.', show_title: true },
	product_showcase: { product_code: 'fixture_product', cta_text: 'View cover', cta_form_code: 'fixture_quote', show_premium: true, show_coverages: true, show_cta: true },
	coverage_grid: { product_code: 'fixture_product', title: 'What is covered', columns: 3 },
	cta: { heading: 'Ready to get covered?', subheading: 'Build your quote today.', button_text: 'Start now', button_form_code: 'fixture_quote' },
	stats: { title: 'Trusted protection', items: [{ value: '25', unit: 'k+', label: 'Customers' }, { value: '98', unit: '%', label: 'Claims supported' }] },
	separator: { height_px: 48, show_line: true },
	faq: { title: 'Frequently asked questions', items: [{ q: 'When does cover start?', a: 'On the date shown in your policy schedule.' }] },
	testimonials: { title: 'Customer stories', columns: 2, items: [{ quote: 'Clear from quote to claim.', author: 'Samira A.', role: 'Policyholder', avatar_url: '/fixtures/avatar.jpg' }] },
	image_text: { heading: 'Support when it matters', image_url: '/fixtures/support.jpg', body: 'Speak with a team that understands your cover.', image_position: 'left', cta_text: 'Learn more', cta_link: '/help' },
	logos: { label: 'Backed by trusted partners', logo_height_px: 48, logos: [{ name: 'Fixture Partner', image_url: '/fixtures/partner.svg', link_url: '#' }] },
	blog_listing: { title: 'Latest guidance', source: 'latest', count: 3, columns: 3, article_slugs: [] },
	how_it_works: { title: 'How it works', steps: [{ title: 'Choose', description: 'Select your cover.', icon: '1' }, { title: 'Protect', description: 'Activate your policy.', icon: '2' }] },
	comparison_table: { title: 'Compare cover', features: ['Emergency support', 'Documents online'], tiers: [{ name: 'Essential', highlighted: false, values: ['Included', 'Included'] }, { name: 'Complete', highlighted: true, values: ['Included', 'Included'] }] },
	pricing_table: { title: 'Choose a plan', plans: [{ name: 'Essential', price: '25', period: 'month', description: 'Core protection', features: ['Digital documents'], cta_text: 'Choose plan', cta_form_code: 'fixture_quote', highlighted: true }] },
	document_downloads: { title: 'Documents', documents: [{ label: 'Policy wording', url: '/fixtures/policy.pdf', file_type: 'PDF', size_label: '1.2 MB' }] },
	trust_badges: { title: 'Your trust matters', badge_height_px: 56, badges: [{ label: 'Secure checkout', image_url: '/fixtures/secure.svg', link_url: '#' }] },
	team_grid: { title: 'Meet the team', columns: 3, people: [{ name: 'Amina Rahman', role: 'Customer care', photo_url: '/fixtures/team.jpg', bio: 'Helping customers understand their cover.' }] },
	video_embed: { title: 'See how it works', caption: 'A short introduction to your cover.', video_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ' },
	app_download: { title: 'Cover in your pocket', subtitle: 'Manage policies and claims anywhere.', ios_url: '#ios', android_url: '#android', qr_image_url: '/fixtures/app-qr.png' },
	branch_locator: { title: 'Find a branch', columns: 2, branches: [{ name: 'City Centre', address: '100 Market Street', phone: '+1 555 0100', hours: 'Mon-Fri, 9-5' }] },
	custom_html: { html: '<div><strong>Tenant-authored content</strong></div>' },
} satisfies { [T in StudioPageSectionType]: StudioPageSectionConfig<T> }

export interface StudioPageComponentDefinition {
	type: StudioPageSectionType
	label: string
	category: 'content' | 'commerce' | 'trust' | 'layout'
	requiresProduct: boolean
	schemaVersion: number
}

export interface TenantPageBuilderConfig {
	contractVersion?: number
	allowedSections?: StudioPageSectionType[]
	defaults?: Partial<{
		[T in StudioPageSectionType]: Partial<StudioPageSectionConfig<T>>
	}>
}

export function resolveStudioPageSections<
	T extends {
		section_type: StudioPageSectionType
		config: StudioSectionStyleConfig & Record<string, unknown>
	},
>(sections: readonly T[], policy?: TenantPageBuilderConfig): T[] {
	if (
		policy?.contractVersion != null &&
		policy.contractVersion > PAGE_BUILDER_CONTRACT_VERSION
	) {
		throw new Error(
			`Page Builder contract v${policy.contractVersion} requires a newer tenant runtime (supports v${PAGE_BUILDER_CONTRACT_VERSION}).`
		)
	}
	const allowed = policy?.allowedSections
		? new Set(policy.allowedSections)
		: null
	return sections
		.filter(section => !allowed || allowed.has(section.section_type))
		.map(section => ({
			...section,
			config: {
				...(policy?.defaults?.[section.section_type] ?? {}),
				...section.config,
			},
		}))
}

export const STUDIO_PAGE_COMPONENTS: readonly StudioPageComponentDefinition[] = [
	{ type: 'hero', label: 'Hero', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'markdown', label: 'Rich text', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'form_embed', label: 'Form embed', category: 'commerce', requiresProduct: false, schemaVersion: 1 },
	{ type: 'product_showcase', label: 'Product showcase', category: 'commerce', requiresProduct: true, schemaVersion: 1 },
	{ type: 'coverage_grid', label: 'Coverage grid', category: 'commerce', requiresProduct: true, schemaVersion: 1 },
	{ type: 'cta', label: 'Call to action', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'stats', label: 'Stats', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'separator', label: 'Separator', category: 'layout', requiresProduct: false, schemaVersion: 1 },
	{ type: 'faq', label: 'FAQ', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'testimonials', label: 'Testimonials', category: 'trust', requiresProduct: false, schemaVersion: 1 },
	{ type: 'image_text', label: 'Image and text', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'logos', label: 'Logo bar', category: 'trust', requiresProduct: false, schemaVersion: 1 },
	{ type: 'blog_listing', label: 'Blog listing', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'how_it_works', label: 'How it works', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'comparison_table', label: 'Comparison table', category: 'commerce', requiresProduct: false, schemaVersion: 1 },
	{ type: 'pricing_table', label: 'Pricing table', category: 'commerce', requiresProduct: false, schemaVersion: 1 },
	{ type: 'document_downloads', label: 'Document downloads', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'trust_badges', label: 'Trust badges', category: 'trust', requiresProduct: false, schemaVersion: 1 },
	{ type: 'team_grid', label: 'Team grid', category: 'trust', requiresProduct: false, schemaVersion: 1 },
	{ type: 'video_embed', label: 'Video embed', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'app_download', label: 'App download', category: 'commerce', requiresProduct: false, schemaVersion: 1 },
	{ type: 'branch_locator', label: 'Branch locator', category: 'content', requiresProduct: false, schemaVersion: 1 },
	{ type: 'custom_html', label: 'Custom HTML', category: 'content', requiresProduct: false, schemaVersion: 1 },
] as const

type AssertNever<T extends never> = T
export type StudioPageComponentRegistryMissingTypes = AssertNever<
	Exclude<StudioPageSectionType, (typeof STUDIO_PAGE_COMPONENTS)[number]['type']>
>
